<template>
  <v-container fluid>
    <v-card class="elevation-11">
      <v-card-title class="accent">
        <h3>Basic Sales Report</h3>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formatStartDate"
                    label="Start Date"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="this.calcMinDate()"
                  :max="this.calcMaxDate()"
                  @input="startDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formatEndDate"
                    label="End Date"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  :min="this.calcMinDate()"
                  :max="this.calcMaxDate()"
                  @input="endDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="4">
              <v-btn
                color="btn-large btn-block primary white--text"
                class="mr-2"
                @click="getBasicSales()"
                :disabled="!valid"
                >Generate</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
    <v-card class="mt-5" elevation-11>
      <v-card-title>
        <v-col cols="12" md="10">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            outlined
            dense
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="btn-large btn-block primary white--text"
            class="mr-2"
            @click="downloadXLS"
            :disabled="!valid"
            >XLS</v-btn
          >
          <vue-json-to-csv
            :json-data="basicsales"
            :csv-title="`AEJ-BSR-${this.startDate}-${this.endDate}`"
          >
            <v-btn color="btn-large btn-block primary white--text"> CSV </v-btn>
          </vue-json-to-csv>
        </v-col>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :loading="loading"
          :search="search"
          :headers="headers"
          :items="basicsales"
          item-key="invkey"
          class="elevation-1"
          loading-text="Retrieving data... please wait"
        >
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import AdminLayout from "@/layouts/AdminLayout";
import api from "../../services/axiosconfig";
import moment from "moment";
import VueJsonToCsv from "vue-json-to-csv";

export default {
  components: {
    VueJsonToCsv,
  },
  data() {
    return {
      valid: true,
      search: "",
      loading: false,
      allCustomers: true,
      ledgerKey: "",

      headers: [
        { text: "Agent", align: "start", value: "agent", width: "150px" },
        {
          text: "Inv No.",
          align: "start",
          value: "invoicenum",
          width: "100px",
        },
        {
          text: "Inv Date",
          align: "start",
          value: "invoicedate",
          width: "150px",
        },
        { text: "PAX Name", align: "start", value: "paxname", width: "200px" },
        {
          text: "Employee No.",
          align: "start",
          value: "accountref",
          width: "125px",
        },
        { text: "TR No.", align: "start", value: "agencyref" },
        { text: "Cost Centre", align: "start", value: "costcentre" },
        { text: "Company Code", align: "start", value: "companycode" },
        { text: "Carrier", align: "end", value: "airline" },
        {
          text: "Ticket No.",
          align: "start",
          value: "ticketno",
          width: "200px",
        },
        {
          text: "Ticket Date",
          align: "start",
          value: "ticketdate",
          width: "150px",
        },
        {
          text: "Departure Date",
          align: "start",
          value: "deptdate",
          width: "150px",
        },
        {
          text: "Return Date",
          align: "start",
          value: "retndate",
          width: "150px",
        },
        { text: "Flight No.", align: "start", value: "flightno" },
        { text: "Basic", align: "end", value: "basic" },
        { text: "Tax", align: "end", value: "tax" },
        { text: "Service", align: "end", value: "service" },
        { text: "Total", align: "end", value: "total" },
        { text: "Credit No.", align: "start", value: "creditno" },
        {
          text: "Credit Date",
          align: "start",
          value: "creditdate",
          width: "150px",
        },
        { text: "Credit Amount", align: "end", value: "creditvalue" },
        { text: "Sector", align: "start", value: "sector", width: "400px" },
        { text: "Class", align: "start", value: "class" },
        { text: "PNR No.", align: "start", value: "pnrno", width: "250px" },
        { text: "DOM/INTL", align: "start", value: "domintl" },
        { text: "Trip Type", align: "start", value: "triptype" },
      ],

      basicsales: [],

      startDateMenu: false,
      endDateMenu: false,

      startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substring(0, 10),

      endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substring(0, 10),
    };
  },

  mounted() {
    this.getCurrentLedgerKey();
  },

  created() {
    this.$emit(`update:layout`, AdminLayout);
  },

  computed: {
    formatStartDate() {
      return `${moment(this.startDate).format("DD-MM-YYYY")}`;
    },

    formatEndDate() {
      return `${moment(this.endDate).format("DD-MM-YYYY")}`;
    },
  },

  methods: {
    clearData() {
      this.basicsales = [];
    },

    getCurrentLedgerKey() {
      this.ledgerKey = localStorage.getItem("LEDG_KEY");
    },

    calcMinDate() {
      return moment()
        .subtract(localStorage.getItem("PERMITTED"), "months")
        .startOf("month")
        .format("YYYY-MM-DD");
    },

    calcMaxDate() {
      return moment().endOf("month").format("YYYY-MM-DD");
    },

    validateData() {
      if (moment(this.startDate).isAfter(this.endDate)) {
        this.$toasted.global.optimus_error({
          message: "Start date must be on or before the end date.",
        });
        return false;
      } else {
        return true;
      }
    },

    dateFormat(dateString) {
      return moment(new Date(dateString)).format("DD/MM/YYYY");
    },

    async getBasicSales() {
      if (this.validateData()) {
        this.basicsales = [];
        try {
          let response = "";
          this.loading = true;
          response = await api.post("/aejmis/api/sales", {
            startdate: this.startDate,
            enddate: this.endDate,
            ledgerkey: this.ledgerKey,
          });

          this.generateBasicSales(response.data);
          this.loading = false;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
          this.loading = false;
          this.$toasted.global.optimus_error({
            message: "Could not retrieve information. Try after sometime.",
          });
        }
      }
    },

    generateBasicSales(rawData) {
      this.basicsales = [];
      rawData.map(
        ({
          invoicenum,
          invoicedate,
          firstname,
          lastname,
          depdate,
          locator,
          accountref,
          agencyref,
          faxnum,
          othertelnum,
          costing,
          creditnote,
          routing,
        }) => {
          this.basicsales.push({
            agent: "AEJ Travels",
            invoicenum: invoicenum,
            invoicedate: this.dateFormat(invoicedate),
            paxname: `${firstname.trim()} ${lastname.trim()}`,
            accountref: accountref,
            agencyref: agencyref,
            costcentre: faxnum,
            companycode: othertelnum,
            airline: this.getAirline(routing),
            ticketno: this.getTicketNum(costing),
            ticketdate: this.getTicketDate(costing),
            deptdate: this.dateFormat(depdate),
            retndate: this.getReturnDate(routing),
            flightno: this.getFlightNum(routing),
            basic: this.calcBasic(costing),
            tax: this.calcTax(costing),
            service: this.calcService(costing),
            total: this.calcInvTotal(costing),
            creditno: creditnote != null ? creditnote.creditnum : "",
            creditdate:
              creditnote != null ? this.dateFormat(creditnote.creditdate) : "",
            creditvalue:
              creditnote != null
                ? parseFloat(creditnote.creditnotevalue).toFixed(2)
                : parseFloat(0).toFixed(2),
            sector: this.encodeSectors(routing),
            class: this.getCabin(routing),
            pnrno: locator,
            domintl: "INTL",
            triptype: this.getTripType(routing),
          });
        }
      );
    },

    getAirline(routes) {
      let first = true;
      let carrier = "";
      routes.forEach((route) => {
        if (route.airline.length > 0 && first) {
          carrier = route.airline;
          first = false;
        }
      });
      return carrier;
    },

    getTicketNum(costing) {
      let ticketno = "";
      let tktFirst = true;

      costing.forEach((costItem) => {
        if (tktFirst) {
          costItem.ticket.forEach((ticketItem) => {
            ticketno = ticketItem.ticketnum;
          });
          tktFirst = false;
        }
      });
      return ticketno;
    },

    getTicketDate(costing) {
      let ticketdt = "";
      let tdtFirst = true;
      costing.forEach((costItem) => {
        if (tdtFirst) {
          costItem.ticket.forEach((ticketItem) => {
            ticketdt = ticketItem.issuedate;
          });
          tdtFirst = false;
        }
      });
      return this.dateFormat(ticketdt);
    },

    getReturnDate(routes) {
      let rtndt = "";
      routes.forEach((route) => {
        if (route.departuredate > "2000-01-01") {
          rtndt = moment(route.departuredate).format("DD/MM/YYYY");
        }
      });
      return rtndt;
    },

    getFlightNum(routes) {
      let first = true;
      let flightno = "";
      routes.forEach((route) => {
        if (route.flight.length > 0 && first) {
          flightno = route.flight;
          first = false;
        }
      });
      return flightno;
    },

    calcBasic(costings) {
      let basic = 0;
      costings.forEach((item) => {
        let tmpDesc = item.description.toUpperCase().trim();
        let isService =
          tmpDesc.includes("TRANSACTION") || tmpDesc.includes("SERVICE");
        if (!isService) {
          basic = basic + item.quantity * (item.gross - item.tax);
        }
      });
      return parseFloat(basic).toFixed(2);
    },

    calcTax(costings) {
      let tax = 0;
      costings.forEach((item) => {
        tax = tax + item.quantity * item.tax;
      });
      return parseFloat(tax).toFixed(2);
    },

    calcService(costings) {
      let service = 0;
      costings.forEach((item) => {
        if (
          item.description.trim().toUpperCase() ===
            "TRANSACTION / SERVICE FEE" ||
          item.description.trim().toUpperCase() ===
            "TRANSACTION / SERVICE CHARGES"
        ) {
          service = service + item.quantity * item.priceeach;
        }
      });
      return parseFloat(service).toFixed(2);
    },

    calcInvTotal(costings) {
      let amount = 0;
      costings.forEach((item) => {
        amount = amount + item.quantity * item.gross;
      });
      return parseFloat(amount).toFixed(2);
    },

    encodeSectors(routes) {
      let first = true;
      let sector = "";
      let sortedRoutes = routes.sort(this.customCompare);
      sortedRoutes.forEach((route) => {
        if (route.airline.length > 0) {
          if (route.source === null || route.target === null) {
            if (first) {
              sector = route.from + "-" + route.destination;
              first = false;
            } else {
              sector = sector + "-" + route.destination;
            }
          } else if (route.source != null && route.target != null) {
            if (first) {
              sector = route.source.citycode + "-" + route.target.citycode;
              first = false;
            } else {
              sector = sector + "-" + route.target.citycode;
            }
          }
        }
      });
      return sector;
    },

    getCabin(routes) {
      let first = true;
      let cabin = "";
      routes.forEach((route) => {
        if (route.class.length > 0 && first) {
          cabin = route.class;
          first = false;
        }
      });
      return cabin;
    },

    getTripType(routes) {
      let tkttype = "";
      routes.forEach((route) => {
        if (route.airline.length > 0) {
          if (
            route.airline.trim() == "9B" ||
            route.airline.trim() == "9F" ||
            route.airline.trim() == "RAIL"
          ) {
            tkttype = "RAIL";
          } else {
            tkttype = "AIR";
          }
        }
      });
      return tkttype;
    },

    // Sort routing key
    customCompare(a, b) {
      return a.routingkey - b.routingkey;
    },

    async downloadXLS() {
      const ExcelJS = require("exceljs");
      const fileSaver = require("file-saver");

      // Create workbook & add worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Airline Ranking");

      // add column headers
      worksheet.columns = [
        { header: "Agent", key: "agent", width: 15 },
        { header: "Invoice No.", key: "invoicenum", width: 15 },
        { header: "Invoice Date", key: "invoicedate", width: 15 },
        { header: "Pax Name", key: "paxname", width: 50 },
        { header: "Employee No.", key: "accountref", width: 20 },
        { header: "GTS/TR No.", key: "agencyref", width: 20 },
        { header: "Cost Centre", key: "faxnum", width: 20 },
        { header: "Company Code", key: "othertelnum", width: 20 },
        { header: "Airline", key: "airline", width: 10 },
        { header: "Ticket No.", key: "ticketno", width: 20 },
        { header: "Ticket Issue Date", key: "ticketdate", width: 20 },
        { header: "Departure Date", key: "deptdate", width: 15 },
        { header: "Return Date", key: "retndate", width: 15 },
        { header: "Flight No.", key: "flightno", width: 15 },
        { header: "Basic", key: "basic", width: 15 },
        { header: "Tax", key: "tax", width: 15 },
        { header: "Service", key: "service", width: 15 },
        { header: "Amount in LC", key: "total", width: 15 },
        { header: "Credit No.", key: "creditno", width: 10 },
        { header: "Credit Date", key: "creditdate", width: 15 },
        { header: "Credit Amount", key: "creditvalue", width: 15 },
        { header: "Sector", key: "sector", width: 50 },
        { header: "Class", key: "class", width: 10 },
        { header: "DOM/INTL", key: "domintl", width: 10 },
        { header: "Trip Type", key: "triptype", width: 10 },
      ];

      // make header bold
      worksheet.getRow(1).font = { name: "Roboto", bold: true };

      // style columns
      worksheet.columns[0].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[1].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[2].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[3].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[4].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[5].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[6].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[7].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[8].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[9].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[10].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[11].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[12].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[13].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[14].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[15].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[16].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[17].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[18].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[19].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[20].alignment = {
        vertical: "middle",
        horizontal: "right",
      };
      worksheet.columns[21].alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      worksheet.columns[22].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[23].alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.columns[24].alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      // Add rows using both the above of rows
      if (this.basicsales.length > 0) {
        let rows = [];
        this.basicsales.forEach((sale) => {
          rows.push({
            agent: sale.agent,
            invoicenum: sale.invoicenum,
            invoicedate: sale.invoicedate,
            paxname: sale.paxname,
            accountref: sale.accountref,
            agencyref: sale.agencyref,
            costcentre: sale.faxnum,
            companycode: sale.othertelnum,
            airline: sale.airline,
            ticketno: sale.ticketno,
            ticketdate: sale.ticketdate,
            deptdate: sale.deptdate,
            retndate: sale.retndate,
            flightno: sale.flightno,
            basic: sale.basic,
            tax: sale.tax,
            service: sale.service,
            total: sale.total,
            creditno: sale.creditno,
            creditdate: sale.creditdate,
            creditvalue: sale.creditvalue,
            sector: sale.sector,
            class: sale.class,
            pnrno: sale.locator,
            domintl: sale.domintl,
            triptype: sale.triptype,
          });
        });

        worksheet.addRows(rows);

        workbook.xlsx.writeBuffer().then((data) => {
          const blob = new Blob([data], { type: "application/octet-stream" });
          fileSaver.saveAs(
            blob,
            `AEJ-BSR-${this.startDate}-${this.endDate}.xlsx`
          );
        });
      }
    },
  },
};
</script>
